import AltRouteOutlinedIcon from '@mui/icons-material/AltRouteOutlined'
import DifferenceOutlinedIcon from '@mui/icons-material/DifferenceOutlined'
import {
	Box,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Tab,
	Tabs,
	Typography,
} from '@mui/material'
import { ReactNode, useEffect, useMemo, useState } from 'react'

import { useAppContext } from '@/hooks'

import { parentCompareTarget } from '../pickDefaultBranch'
import { BranchRecord } from '../types'
import { BranchCompareView } from './BranchCompareView'

type BranchDetailCompareProps = {
	branch: BranchRecord
	branches: BranchRecord[]
	requestedTargetId?: string | null
}

type DetailTab = 'cherry' | 'compare'

const tabPanelSx = {
	display: 'flex',
	flexDirection: 'column',
	flex: 1,
	minHeight: 0,
}

export const BranchDetailCompare = ({
	branch,
	branches,
	requestedTargetId = null,
}: BranchDetailCompareProps) => {
	const { t } = useAppContext()
	const options = useMemo(
		() =>
			branches.filter(
				(item) => item.status !== 'DELETED' && item.id !== branch.id,
			),
		[branch.id, branches],
	)
	const initialTarget =
		requestedTargetId ?? parentCompareTarget(branch, branches)
	const [tab, setTab] = useState<DetailTab>('compare')
	const [compareId, setCompareId] = useState<string | null>(initialTarget)
	const [pickId, setPickId] = useState<string | null>(initialTarget)

	useEffect(() => {
		if (requestedTargetId) setCompareId(requestedTargetId)
	}, [requestedTargetId])

	const compareTarget = options.find((item) => item.id === compareId) ?? null
	const pickTarget = options.find((item) => item.id === pickId) ?? null

	const branchSelect = (
		value: string | null,
		onChange: (id: string) => void,
		label: string,
		testId: string,
	): ReactNode => (
		<FormControl size="small" sx={{ width: 220 }}>
			<InputLabel id={`${testId}-label`}>{label}</InputLabel>
			<Select
				labelId={`${testId}-label`}
				label={label}
				displayEmpty
				value={value ?? ''}
				onChange={(event) => onChange(String(event.target.value))}
				data-testid={testId}
			>
				{!value ? (
					<MenuItem value="" disabled>
						{t('BRANCHES_COMPARE_SELECT')}
					</MenuItem>
				) : null}
				{options.map((item) => (
					<MenuItem key={item.id} value={item.id}>
						{item.name}
					</MenuItem>
				))}
			</Select>
		</FormControl>
	)

	const panel = (
		target: BranchRecord | null,
		select: ReactNode,
		variant: DetailTab,
	) =>
		target ? (
			<BranchCompareView
				from={branch}
				targetPickedByUser={target.id !== branch.sourceBranchId}
				targetSelect={select}
				to={target}
				variant={variant}
			/>
		) : (
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
				{select}
				<Typography
					data-testid="branch-compare-empty"
					sx={{ fontSize: 14, color: 'text.secondary' }}
				>
					{options.length
						? t('BRANCHES_COMPARE_PROMPT')
						: t('BRANCHES_COMPARE_NO_TARGET')}
				</Typography>
			</Box>
		)

	return (
		<Box
			data-testid="branch-detail-compare"
			sx={{
				bgcolor: '#fff',
				border: '1px solid #E6E8EA',
				borderRadius: '14px',
				overflow: 'hidden',
				display: 'flex',
				flexDirection: 'column',
				flex: 1,
				minHeight: 0,
			}}
		>
			<Tabs
				value={tab}
				onChange={(_event, value: DetailTab) => setTab(value)}
				sx={{
					px: 1.5,
					borderBottom: 1,
					borderColor: 'divider',
					minHeight: 48,
					'& .MuiTab-root': { minHeight: 48, textTransform: 'uppercase' },
					'& .Mui-selected': { color: '#265560' },
					'& .MuiTabs-indicator': { bgcolor: '#265560' },
				}}
			>
				<Tab
					value="compare"
					icon={<DifferenceOutlinedIcon />}
					iconPosition="start"
					label={t('BRANCHES_COMPARE')}
					data-testid="branch-detail-tab-compare"
				/>
				<Tab
					value="cherry"
					icon={<AltRouteOutlinedIcon />}
					iconPosition="start"
					label={t('BRANCHES_CHERRY_PICK_TAB')}
					data-testid="branch-detail-tab-cherry"
				/>
			</Tabs>
			<Box
				sx={{
					px: 2,
					pt: 2,
					pb: 0,
					display: 'flex',
					flexDirection: 'column',
					flex: 1,
					minHeight: 0,
					overflow: 'hidden',
				}}
			>
				<Box
					sx={{
						...tabPanelSx,
						display: tab === 'compare' ? 'flex' : 'none',
					}}
				>
					{panel(
						compareTarget,
						branchSelect(
							compareId,
							setCompareId,
							t('BRANCHES_COMPARE_WITH_LABEL'),
							'branch-compare-select',
						),
						'compare',
					)}
				</Box>
				<Box
					sx={{
						...tabPanelSx,
						display: tab === 'cherry' ? 'flex' : 'none',
					}}
				>
					{panel(
						pickTarget,
						branchSelect(
							pickId,
							setPickId,
							t('BRANCHES_CHERRY_PICK_FROM'),
							'branch-cherry-pick-select',
						),
						'cherry',
					)}
				</Box>
			</Box>
		</Box>
	)
}
